import {Country} from '@hconnect/apiclient'
import {flagPathByCountryCode} from '@hconnect/uikit'
import React from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {SelectGrouped} from '../../../pages/ManageUser/components/SelectGrouped'
import {UserInformationInputSkeleton} from '../Skeleton/UserInformationInputSkeleton'

import {UserInformationProps} from './types'

type UserCountryInputProps<T extends FieldValues> = UserInformationProps<T> & {
  countriesIds: string[]
  selectedCountry?: Country
}

export const UserCountryInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  countriesIds,
  isLoading,
  selectedCountry,
  name
}: UserCountryInputProps<T>) => {
  const {t} = useTranslation()

  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  const countriesData = window.intlTelInputGlobals.getCountryData()

  const countryDataFilter = (countryId: string) =>
    countryId === 'MOCK'
      ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
      : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

  const formattedCountryData = countriesIds
    .filter((countryId) => countryDataFilter(countryId))
    .map((countryId) => {
      return {
        label: countryDataFilter(countryId)?.name ?? '',
        value: countryDataFilter(countryId)?.iso2.toUpperCase() ?? ''
      }
    })
    .sort((current, next) => {
      if (current.label < next.label) {
        return -1
      }
      if (current.label > next.label) {
        return 1
      }
      return 0
    })

  return (
    <Controller
      name={name}
      control={formMethods.control}
      rules={{validate: (value) => (value ? true : t('errorMessages.FIELD_REQUIRED'))}}
      render={({field, fieldState}) => (
        <SelectGrouped
          {...field}
          inputLabel={t('userInformationForm.country')}
          value={(field.value as string) || '-'}
          data-test-id="userInformationForm-country"
          onChange={(value) => {
            formMethods.setValue('country' as any, value as any, {
              shouldValidate: true
            })
            formMethods.setValue('defaultBranding' as any, '' as any, {
              shouldValidate: true
            })
            formMethods.setValue('primaryOrganisationalUnit' as any, '' as any, {
              shouldValidate: true
            })
            formMethods.setValue('marketId' as any, '' as any, {
              shouldValidate: true
            })
            formMethods.setValue('defaultLocale' as any, '' as any, {
              shouldValidate: true
            })
          }}
          error={fieldState.error ? fieldState.error.message : ''}
          options={formattedCountryData}
          showSearch={true}
          searchPlaceholder={`Select ${t('userInformationForm.country')}`}
          isReadOnly={readonlyMode}
        />
      )}
    />
  )
}
